<template>
  <div ref="consumptionScrollContainer" class="d-flex justify-content-center" style="overflow-y: auto; overflow-x: hidden;">
    <div style="width: 95%">
      <div class="d-flex justify-content-center mb-2">
      <h6>{{chartTitle}}</h6>
      </div>
        <span v-show="targetGroups.length > 2" style="color: red; font-size: smaller;" class="ml-5">Visar max 2 målgrupper samtidigt</span>
    <div class="d-flex flex-wrap align-items-start ml-5 mb-1 justify-content-start">
      <div class="mb-3 mr-3">
        <CheckboxDropdownOptions
          class="provider-dropdown"
          :dropdown-info-text="dropdownInfoText"
          :initial-selection="initialSelectedGroup"
          label-type-name="Målgrupper"
          :options-array="targetGroups"
          :max-selectable="2"
          @onInputChanged="updateSelectedTargetGroups"
          :disabled="!isDataLoaded"
        />
      </div>
      <div class="d-inline-flex mb-3 mr-3">
        <b-button-group class="toggle-button-group">
          <b-button
            size="sm"
            class="px-4 text-nowrap"
            :disabled="!isDataLoaded"
            :variant="activeConsumptionType === 'PerTitleCode' ? 'primary' : 'outline-secondary'"
            @click="setConsumptionMode('PerTitleCode')"
          >
            Per program
          </b-button>
          <b-button
            size="sm"
            class="px-4 text-nowrap"
            :disabled="!isDataLoaded"
            :variant="activeConsumptionType === 'PerDay' ? 'primary' : 'outline-secondary'"
            @click="setConsumptionMode('PerDay')"
          >
            Per Dag
          </b-button>
          <b-button
            v-if="hasMultipleDaysData"
            size="sm"
            class="px-4 text-nowrap"
            :disabled="!isDataLoaded"
            :variant="activeConsumptionType === 'AggregatedDays' ? 'primary' : 'outline-secondary'"
            @click="setConsumptionMode('AggregatedDays')"
          >
            Sammanslagna dagar
          </b-button>
        </b-button-group>
      </div>
      <div class="d-inline-flex mb-3">
        <b-button-group class="toggle-button-group">
          <b-button
            size="sm"
            class="px-4 text-nowrap"
            :disabled="!isDataLoaded"
            :variant="showCumulativeData === false ? 'primary' : 'outline-secondary'"
            @click="toggleCumulativeData(false)"
          >
            Normal Data
          </b-button>
          <b-button
            size="sm"
            class="px-4 text-nowrap"
            :disabled="!isDataLoaded"
            :variant="showCumulativeData === true ? 'primary' : 'outline-secondary'"
            @click="toggleCumulativeData(true)"
          >
            Kumulativ Data
          </b-button>
        </b-button-group>
      </div>
    </div>
    <div v-if="isDataLoaded">
      <b-row class="mb-0">
        <b-col>
          <div class="minimap-chart-container">
            <BaseMinimapLineDiagram
              :chart-data="filteredChartData"
              :max-range-y="chartMaxDataValue"
              class="minimap-diagram"
            />
            <RangeSlider
              :labels="consumptionMajorXLabels"
              class="minimap-slider"
              :externalMin="rangeMinPercentage"
              :externalMax="rangeMaxPercentage"
              @on-range-change="handleRangeChange"
            />
          </div>
        </b-col>
      </b-row>
      <BaseLineDiagram
        :style="{ width: '100%', height: chartHeight }"
        :chart-data="filteredZoomChartData"
        legend-position="bottom"
        :max-range-y="chartMaxDataValue"
        class="line-diagram-component"
      />
    </div>
    <div
      v-else-if="isDataLoading"
      class="text-center mb-2 spinner-container"
    >
      <b-spinner
        variant="primary"
        type="grow"
        label="Spinning"
      />
      <div>
        <small class="text-muted">Laddar konsumtionsmönster...</small>
      </div>
    </div>
    <div
      v-else
      class="text-center text-danger mb-2"
    >
      <p>{{ errorMessage }}</p>
    </div>
    </div>
  </div>
</template>

<script>
import BaseLineDiagram from '../../Shared/BaseLineDiagram.vue';
import BaseMinimapLineDiagram from '../../Shared/BaseMinimapLineDiagram.vue';
import CheckboxDropdownOptions from '../../Shared/CheckboxDropdownOptions.vue';
import RangeSlider from '@/components/Shared/RangeSlider';
import dayjs from 'dayjs';
import { consumptionModeMapping } from '@/constants/appConstants';

export default {
  name: 'ProgramConsumption',
  components: {
    BaseLineDiagram,
    BaseMinimapLineDiagram,
    CheckboxDropdownOptions,
    RangeSlider,
  },
  data() {
    return {
      dropdownInfoText: "Målgrupper <span style='font-size: smaller;'>(max 2 åt gången)</span>",
      selectedTargetGroups: [],
      activeConsumptionType: 'PerDay',
      showCumulativeData: false,
      showAggregated: false,
      minChartIndex: 0,
      maxChartIndex: 0,
      rangeMinPercentage: 0,
      rangeMaxPercentage: 100
    };
  },
  computed: {
    currentModeConfig() {
      const modeConfig = consumptionModeMapping[this.activeConsumptionType];
      return this.showCumulativeData ? modeConfig.cumulative : modeConfig.normal;
    },
    currentChartData() {
      const state = this.currentModeConfig.state;
      return this.$store.getters[`consumptionResults/${state}`].chartData;
    },
    isDataLoaded() {
      const state = this.currentModeConfig.state;
      return this.$store.getters[`consumptionResults/${state}`].state === 'LOADED';
    },
    isDataLoading() {
      const state = this.currentModeConfig.state;
      return this.$store.getters[`consumptionResults/${state}`].state === 'LOADING';
    },
    targetGroups() {
      return this.$store.getters['consumptionResults/consumptionTargetGroups'];
    },
    hasMultipleDaysData() {
      return this.$store.getters['consumptionResults/hasMultipleDaysData'];
    },
    initialSelectedGroup() {
      return this.targetGroups;
    },
    consumptionPeriod() {
      const startDate = this.$store.getters['consumptionResults/consumptionResultsQuery']
        .start_date;
      const endDate = this.$store.getters['consumptionResults/consumptionResultsQuery'].end_date;
      return `${startDate} - ${endDate}`;
    },
    consumptionMajorXLabels() {
      if (this.activeConsumptionType !== "PerTitleCode") {
        return [
          "02:00", "06:00", "10:00", "14:00", "18:00", "22:00"
        ];
      }
      const startDateString = this.$store.getters['consumptionResults/consumptionResultsQuery']
        .start_date;
      const endDateString = this.$store.getters['consumptionResults/consumptionResultsQuery']
        .end_date;

      const startDate = dayjs(startDateString);
      const endDate = dayjs(endDateString);
      let currentDate = startDate;
      const dates = [];

      while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
        dates.push(currentDate.format('YYYY-MM-DD'));
        currentDate = currentDate.add(1, 'day');
      }

      return dates;
    },
    chartTitle() {
      return `Konsumtionsmönster för räckvidd, ${this.consumptionPeriod}`;
    },
    chartHeight() {
      // eslint-disable-next-line no-mixed-operators
      return `${this.chartDatasetCount * 2.25 + 600}px`;
    },
    chartDatasetCount() {
      return this.filteredChartData?.datasets?.length;
    },
    chartMaxDataValue() {
      let maxDataPoints = this.filteredChartData.datasets.map((dataset) =>
        Math.max(...dataset.data),
      );
      let maxDataPoint = Math.max(...maxDataPoints);

      if (maxDataPoint <= 0) return 10;

      let power = Math.floor(Math.log10(maxDataPoint));
      let roundingFactor = Math.pow(10, power) / 10;
      // Adjust for numbers less than 10
      if (maxDataPoint < 10) {
        roundingFactor = 1;
      }
      let rounded = Math.floor(maxDataPoint / roundingFactor) * roundingFactor;
      let difference = maxDataPoint - rounded;
      let threshold = 0.9 * roundingFactor;
      // Check if the difference is greater than or equal to the threshold
      if (difference >= threshold) {
        return rounded + 2 * roundingFactor;
      } else {
        return rounded + roundingFactor;
      }
    },
    filteredChartData() {
      if (!this.isDataLoaded) {
        return { labels: [], datasets: [] };
      }
      const chartData = this.currentChartData;
      // Filter datasets based on selected target groups
      const filteredDatasets = chartData.datasets.filter((dataset) =>
        this.selectedTargetGroups.includes(dataset.targetGroup),
      );
      return {
        labels: chartData.labels,
        datasets: filteredDatasets,
      };
    },
    filteredZoomChartData() {
      const chartData = this.filteredChartData;
      if (!chartData || !chartData.labels || !chartData.datasets) {
        return { labels: [], datasets: [] };
      }
      // Apply zoom logic
      const zoomedLabels = chartData.labels.slice(this.minChartIndex, this.maxChartIndex);
      const zoomedDatasets = chartData.datasets.map((dataset) => {
        return {
          ...dataset,
          data: dataset.data.slice(this.minChartIndex, this.maxChartIndex),
        };
      });

      return {
        labels: zoomedLabels,
        datasets: zoomedDatasets,
      };
    },
    errorMessage() {
        return 'Det inträffade ett fel vid inläsningen av konsumtionsmönster. Var god utför en ny sökning.';
    },
  },
  watch: {
    '$store.state.consumptionResults.showToastError'() {
      if (this.$store.state.consumptionResults.showToastError) {
        this.$bvToast.toast('Okänt serverfel vid hämtning av konsumtionsmönster för program', {
          title: 'Fel',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
          bodyOutputType: 'trustedHtml',
        });
        this.$store.state.reachResults.showToastError = false;
      }
    },
    isDataLoaded() {
      this.$nextTick(() => {
        const container = this.$refs.consumptionScrollContainer;
        container.lastElementChild.scrollIntoView({ behavior: 'smooth' });
      })
    },
  },
  created() {
    this.selectedTargetGroups = this.targetGroups;
  },
  methods: {
    updateSelectedTargetGroups(selectedGroups) {
      this.selectedTargetGroups = selectedGroups;
      return selectedGroups;
    },
    async setConsumptionMode(button) {
      if (this.activeConsumptionType === button) return;
      this.activeConsumptionType = button;
      this.resetZoom();
      await this.fetchDataIfNeeded();
    },
    async toggleCumulativeData(isCumulative) {
      if (this.showCumulativeData === isCumulative) return;
      this.showCumulativeData = isCumulative;
      this.resetZoom();
      await this.fetchDataIfNeeded();
    },
    async fetchDataIfNeeded() {
      const config = this.currentModeConfig;
      const state = this.$store.getters[`consumptionResults/${config.state}`];
      if (state.state !== 'LOADED') {
        await this.$store.dispatch(`consumptionResults/${config.action}`, {
          mutationName: config.mutation,
          getCumulativeMeasurements: config.getCumulativeMeasurements,
          getAggregatedData: config.getAggregatedData,
        });
      }
    },
    onThisTabClick() {
      this.activeConsumptionType = 'PerDay';
      this.showAggregated = false;
      this.showCumulativeData = false;
      this.resetZoom();
    },
    resetZoom() {
      this.$nextTick(() => {
        this.minChartIndex = 0;
        this.maxChartIndex = this.filteredChartData.labels.length
        this.rangeMinPercentage = 0
        this.rangeMaxPercentage = 100
      })
    },
    handleRangeChange(range) {
      // Calculate the indices based on the slider's percentage range
      this.rangeMinPercentage = range.minHandlePercent
      this.rangeMaxPercentage = range.maxHandlePercent
       // (1 + (1 / this.filteredChartData.datasets[0].data.length)) is for finetuning alignment between the slider vs chart
      this.minChartIndex = Math.round(
        range.minHandlePercent / 100 * (1 + (1 / this.filteredChartData.datasets[0].data.length)) * (this.filteredChartData.labels.length - 1),
      );
      this.maxChartIndex = Math.round(
        range.maxHandlePercent / 100 * (1 + (1 / this.filteredChartData.datasets[0].data.length)) * (this.filteredChartData.labels.length - 1)) + 1;
    },
    calculateMaxY(maxDataPoint) {
      if (maxDataPoint <= 0) {
        return 0;
      }
      // Determine the order of magnitude of the maximum data point
      const orderOfMagnitude = Math.floor(Math.log10(maxDataPoint));
      const magnitudeValue = Math.pow(10, orderOfMagnitude);
      // Define a rounding factor based on the magnitude
      let roundFactor;
      const normalizedMax = maxDataPoint / magnitudeValue;
      if (normalizedMax < 1.5) {
        roundFactor = 0.2;
      } else if (normalizedMax < 3) {
        roundFactor = 0.5;
      } else if (normalizedMax < 7.5) {
        roundFactor = 1;
      } else {
        roundFactor = 2;
      }
      // Round up the maximum data point to the nearest multiple of the rounding factor
      return (
        Math.ceil(maxDataPoint / (magnitudeValue * roundFactor)) * magnitudeValue * roundFactor
      );
    },
  },
};
</script>

<style scoped>
.color-grid {
  display: flex;
  flex-wrap: wrap;
}

.color-block {
  width: 60px;
  height: 60px;
  margin: 5px;
  border: 1px solid #000;
}
.chart-title {
  font-size: 1rem;
  color: black;
}

.provider-dropdown {
  min-width: 20rem;
}

.toggle-button-group > .btn:first-child {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.toggle-button-group > .btn:first-child {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.toggle-button-group > .btn:last-child {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
@media (max-width: 1900px) {
  .line-diagram-component {
    margin-top: 2rem;
  }
}
.minimap-chart-container {
  margin-top: 3rem;
  position: relative;
  width: calc(100% - 3.4rem);
  height: 4rem;
  left: 3rem;
}

.minimap-diagram {
  position: absolute;
  width: 100%;
  top: 0;
  left: -8px;
  margin: 0;
  filter: grayscale(100%);
}

.minimap-slider {
  position: absolute;
  width: calc(100% - 0.5rem);
  top: -2.4rem;
}
</style>
